// src/components/gufram/SvarFilterWrapperSleekV2.jsx
// Completely redesigned with proper DaisyUI styling and spacious layout
import React, { useState } from 'react';
import {
  Search,
  Package,
  Palette,
  Layers,
  Maximize2,
  ShieldCheck,
  Zap,
  Feather,
  CalendarDays
} from 'lucide-react';

const createDefaultFilters = () => ({
  search: '',
  name: '',
  warehouse: [],
  dominantColor: [],
  usage: [],
  usageCategory: [],
  size: [],
  material: [],
  condition: [],
  consumable: [],
  historicalPeriod: [],
  historicalRegion: []
});

const normalizeList = (value) => {
  if (Array.isArray(value)) {
    return value.map(v => String(v).trim()).filter(Boolean);
  }
  if (!value) return [];
  if (typeof value === 'string') {
    return value.split(',').map(v => v.trim()).filter(Boolean);
  }
  return [String(value)];
};

const gatherUnique = (items, accessor, asArray = false) => {
  const values = new Set();
  items.forEach(item => {
    const source = item._original || item;
    const result = accessor(source);
    if (!result) return;
    const candidates = asArray ? normalizeList(result) : [result];
    candidates.forEach(val => {
      if (val) values.add(val);
    });
  });
  return Array.from(values).sort();
};

const SearchIcon = ({ className }) => (
  <Search className={["absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400", className].join(" ")} />
);

const SectionCard = ({ title, action, children }) => (
  <div className="flex flex-col gap-3 border border-gray-200 bg-white rounded-2xl p-4 shadow-[0_10px_35px_rgba(15,23,42,0.08)]">
    <div className="flex items-center justify-between">
      <h3 className="font-semibold text-sm uppercase tracking-[0.2em] text-gray-600">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

export function SvarFilterWrapperSleekV2({ 
  items = [], 
  filters, 
  onFiltersChange,
  resultsCount 
}) {
  const [localFilters, setLocalFilters] = useState(() => filters || createDefaultFilters());
  const [activeTab, setActiveTab] = useState('search');

  const warehouses = gatherUnique(items, source => source.warehouse);
  const colors = gatherUnique(items, source => source.colors, true);
  const usages = gatherUnique(items, source => source.usage);
  const usageCategories = gatherUnique(items, source => source.usageCategory || source.usage_category_name);
  const sizes = gatherUnique(items, source => source.size);
  const materials = gatherUnique(items, source => source.material, true);
  const conditions = gatherUnique(items, source => source.condition);
  const consumables = gatherUnique(items, source => source.consumable);
  const historicalPeriods = gatherUnique(items, source => source.historicalPeriod);
  const historicalRegions = gatherUnique(items, source => source.historicalRegion);

  const handleTextChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const handleToggle = (field, value) => {
    const current = localFilters[field] || [];
    const newValues = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    const newFilters = { ...localFilters, [field]: newValues };
    setLocalFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const clearFilters = () => {
    const emptyFilters = createDefaultFilters();
    setLocalFilters(emptyFilters);
    onFiltersChange?.(emptyFilters);
  };

  const clearCategory = (field) => {
    const newFilters = { ...localFilters, [field]: [] };
    setLocalFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const usageCount = localFilters.usage.length + localFilters.usageCategory.length;
  const historyCount = localFilters.historicalPeriod.length + localFilters.historicalRegion.length;
  const activeCounts = {
    search: (localFilters.search ? 1 : 0) + (localFilters.name ? 1 : 0),
    warehouse: localFilters.warehouse.length,
    color: localFilters.dominantColor.length,
    usage: usageCount,
    size: localFilters.size.length,
    material: localFilters.material.length,
    condition: localFilters.condition.length,
    consumable: localFilters.consumable.length,
    history: historyCount
  };

  const totalActive = Object.values(activeCounts).reduce((a, b) => a + b, 0);

  if (!items || items.length === 0) {
    return (
      <div className="bg-base-200 rounded-xl p-6 text-center border border-base-300">
        <p className="text-base-content/70 text-sm">No items available for filtering</p>
      </div>
    );
  }

  const chipClasses = (isActive) => [
    "flex items-center justify-center px-4 py-1 text-base font-semibold transition-all duration-150 whitespace-nowrap",
    "border-2 rounded-full",
    isActive
      ? "bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/30"
      : "bg-gray-100 text-gray-700 border-gray-200 hover:border-blue-400 hover:text-blue-700"
  ]
    .join(" ")
    .trim();

  const LinkButton = ({ children, onClick, className }) => (
    <button
      type="button"
      onClick={onClick}
      className={[
        "text-xs font-semibold uppercase tracking-[0.3em] text-blue-600 hover:text-blue-700",
        className
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </button>
  );

  const renderChips = (options, field, activeValues) => (
    <div className="flex flex-wrap gap-2 py-1">
      {options.map((value) => {
        const isActive = activeValues.includes(value);
        return (
          <button
            key={`${field}-${value}`}
            type="button"
            onClick={() => handleToggle(field, value)}
            className={chipClasses(isActive)}
          >
            {value}
          </button>
        );
      })}
    </div>
  );

  const allWarehousesSelected =
    warehouses.length > 0 &&
    warehouses.every((w) => localFilters.warehouse.includes(w));
  const toggleWarehouseSelectAll = () => {
    const newValues = allWarehousesSelected ? [] : [...warehouses];
    const newFilters = { ...localFilters, warehouse: newValues };
    setLocalFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  return (
    <div className="flex flex-col h-full text-base">
      <div className="bg-base-200 px-4 py-3 flex justify-between items-center border-b border-base-300 flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-base-content">Filters</span>
          {totalActive > 0 && (
            <span className="text-sm font-semibold text-blue-600">{totalActive} selected</span>
          )}
        </div>
        <button
          onClick={clearFilters}
          type="button"
          className={[
            "px-4 py-2 text-sm font-semibold rounded-lg border transition-colors duration-150",
            totalActive === 0
              ? "border-gray-200 text-gray-400 cursor-not-allowed"
              : "border-blue-600 text-blue-600 hover:bg-blue-50"
          ].join(" ")}
          disabled={totalActive === 0}
        >
          Clear
        </button>
      </div>

      <div className="bg-base-200/50 flex-shrink-0">
        <div className="flex gap-1 overflow-x-auto" style={{ scrollbarGutter: 'stable', padding: '1em'}}>
          {[
            { id: 'search', icon: Search, label: 'Search', count: activeCounts.search },
            { id: 'warehouse', icon: Package, label: 'Warehouse', count: activeCounts.warehouse },
            { id: 'color', icon: Palette, label: 'Color', count: activeCounts.color },
            { id: 'usage', icon: Layers, label: 'Usage', count: activeCounts.usage },
            { id: 'size', icon: Maximize2, label: 'Size', count: activeCounts.size },
            { id: 'material', icon: Feather, label: 'Material', count: activeCounts.material },
            { id: 'condition', icon: ShieldCheck, label: 'Condition', count: activeCounts.condition },
            { id: 'consumable', icon: Zap, label: 'Consumable', count: activeCounts.consumable },
            { id: 'history', icon: CalendarDays, label: 'History', count: activeCounts.history }
          ].map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={[
                  "flex items-center gap-2 flex-shrink-0 px-4 py-1 text-base font-bold transition-all duration-150",
                  "border-2 rounded-full",
                  isActive
                    ? "bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/30"
                    : "bg-white text-gray-700 border-gray-200 hover:border-blue-400 hover:text-blue-700"
                ].join(" ")}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span className="badge badge-xs">{tab.count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 flex-1 overflow-y-auto space-y-6">
        {activeTab === 'search' && (
          <div className="animate-fade-in flex flex-col gap-4">
            <SectionCard
              title="Search props"
              action={<LinkButton onClick={() => handleTextChange('search', '')}>Reset</LinkButton>}
            >
              <div className="relative">
                <SearchIcon />
                <input
                  value={localFilters.search}
                  onChange={(e) => handleTextChange('search', e.target.value)}
                  placeholder="Search props by name, org, tag, or use"
                  className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
              </div>
            </SectionCard>
            <SectionCard title="Filter by name">
              <input
                value={localFilters.name}
                onChange={(e) => handleTextChange('name', e.target.value)}
                placeholder="Exact name match"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
            </SectionCard>
          </div>
        )}

        {activeTab === 'warehouse' && (
          <SectionCard
            title="Which local stacks to include"
            action={
              <div className="flex items-center gap-3">
                <LinkButton onClick={() => clearCategory('warehouse')}>Clear</LinkButton>
                <LinkButton onClick={toggleWarehouseSelectAll}>
                  {allWarehousesSelected ? 'Deselect All' : 'Select All'}
                </LinkButton>
              </div>
            }
          >
            <div className="flex flex-col gap-3">
              <div className="relative">
                <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search warehouses..."
                  className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  value=""
                  readOnly
                />
              </div>
              {renderChips(warehouses, 'warehouse', localFilters.warehouse)}
            </div>
          </SectionCard>
        )}

        {activeTab === 'color' && (
          <SectionCard
            title="Colors"
            action={
              <LinkButton onClick={() => clearCategory('dominantColor')}>Clear</LinkButton>
            }
          >
            {renderChips(colors, 'dominantColor', localFilters.dominantColor)}
          </SectionCard>
        )}

        {activeTab === 'usage' && (
          <div className="flex flex-col gap-4">
            <SectionCard
              title="Usage"
              action={<LinkButton onClick={() => clearCategory('usage')}>Clear</LinkButton>}
            >
              {renderChips(usages, 'usage', localFilters.usage)}
            </SectionCard>
            {usageCategories.length > 0 && (
              <SectionCard
                title="Usage Category"
                action={
                  <LinkButton onClick={() => clearCategory('usageCategory')}>Clear</LinkButton>
                }
              >
                {renderChips(usageCategories, 'usageCategory', localFilters.usageCategory)}
              </SectionCard>
            )}
          </div>
        )}

        {activeTab === 'size' && (
          <SectionCard
            title="Size"
            action={<LinkButton onClick={() => clearCategory('size')}>Clear</LinkButton>}
          >
            {renderChips(sizes, 'size', localFilters.size)}
          </SectionCard>
        )}

        {activeTab === 'material' && (
          <SectionCard
            title="Materials"
            action={<LinkButton onClick={() => clearCategory('material')}>Clear</LinkButton>}
          >
            {renderChips(materials, 'material', localFilters.material)}
          </SectionCard>
        )}

        {activeTab === 'condition' && (
          <SectionCard
            title="Condition"
            action={<LinkButton onClick={() => clearCategory('condition')}>Clear</LinkButton>}
          >
            {renderChips(conditions, 'condition', localFilters.condition)}
          </SectionCard>
        )}

        {activeTab === 'consumable' && (
          <SectionCard
            title="Consumable"
            action={<LinkButton onClick={() => clearCategory('consumable')}>Clear</LinkButton>}
          >
            {renderChips(consumables.length ? consumables : ['Yes', 'No'], 'consumable', localFilters.consumable)}
          </SectionCard>
        )}

        {activeTab === 'history' && (
          <div className="flex flex-col gap-4">
            {historicalPeriods.length > 0 && (
              <SectionCard
                title="Historical Period"
                action={<LinkButton onClick={() => clearCategory('historicalPeriod')}>Clear</LinkButton>}
              >
                {renderChips(historicalPeriods, 'historicalPeriod', localFilters.historicalPeriod)}
              </SectionCard>
            )}
            {historicalRegions.length > 0 && (
              <SectionCard
                title="Historical Region"
                action={<LinkButton onClick={() => clearCategory('historicalRegion')}>Clear</LinkButton>}
              >
                {renderChips(historicalRegions, 'historicalRegion', localFilters.historicalRegion)}
              </SectionCard>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
